import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, CircularProgress, Box, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import {
  treatmentPlanService,
  PublicTreatmentPlanResponse,
} from "../../services/treatmentPlanService";
import { AppLogoIcon } from "../../components/Icons";
import PersonsInfo from "../../components/PersonsInfo";
import {
  TreatmentAppointment,
  TreatmentAppointmentsTableHeader,
} from "../TreatmentPlanPage/components";
import { colors } from "../../theme/colors";
import { useModal, SignatureModalContent } from "../../components/Modal";
import { generateSignedTreatmentPlanPdf } from "../../utils/downloadTreatmentPlanPdf";
import VerificationCodeInput from "../../components/VerificationCodeInput";
import { FormErrorContainer } from "../../components/FormErrorContainer";
import AuthPageLayout from "../../components/layout/AuthPageLayout";
import { formatTotalFeeWithCents } from "../../utils/appointmentDisplayUtils";
import {
  PageContainer,
  ContentWrapper,
  StickyHeader,
  LogoContainer,
  PageTitle,
  DateInfo,
  Section,
  SectionTitle,
  SectionContent,
  StepsContainer,
  TotalFeeContainer,
  TotalFeeLabel,
  TotalFeeValue,
  ActionButtonsContainer,
  Footer,
  FooterDate,
  FooterClinicName,
  FooterContacts,
  LoadingContainer,
  ErrorContainer,
  ErrorText,
  OtpVerificationContainer,
  OtpVerificationTitle,
  OtpVerificationSubtitle,
  OtpVerificationForm,
  OtpVerificationActions,
} from "./TreatmentPlanReviewPage.styled";

const CACHE_KEY_PREFIX = "treatment_plan_review_";

const getCacheKey = (tkn: string) => `${CACHE_KEY_PREFIX}${tkn}`;

const getCachedData = (tkn: string): PublicTreatmentPlanResponse | null => {
  try {
    const cached = sessionStorage.getItem(getCacheKey(tkn));
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error("Error reading from cache:", e);
  }
  return null;
};

const setCachedData = (tkn: string, data: PublicTreatmentPlanResponse) => {
  try {
    sessionStorage.setItem(getCacheKey(tkn), JSON.stringify(data));
  } catch (e) {
    console.error("Error writing to cache:", e);
  }
};

const TreatmentPlanReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();
  const { openModal, closeModal, updateModal } = useModal();
  const { token } = useParams<{ token: string }>();

  const [treatmentPlan, setTreatmentPlan] =
    useState<PublicTreatmentPlanResponse | null>(() =>
      token ? getCachedData(token) : null,
    );
  const [loading, setLoading] = useState(() => !treatmentPlan);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  // OTP verification state
  const [requiresOtp, setRequiresOtp] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Send OTP code
  const sendOtpCode = async () => {
    if (!token || otpSending) return;

    try {
      setOtpSending(true);
      setOtpError(null);
      await treatmentPlanService.sendOtp(token);

      // Start 60 second cooldown for resend
      setResendCooldown(60);
      resendTimerRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (resendTimerRef.current) {
              clearInterval(resendTimerRef.current);
              resendTimerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      const axiosError = err as AxiosError<{ detail?: string }>;
      setOtpError(
        axiosError.response?.data?.detail ||
          t("treatmentPlanReview.otpSendError"),
      );
      console.error("Error sending OTP:", err);
    } finally {
      setOtpSending(false);
    }
  };

  // Verify OTP and fetch treatment plan
  const verifyOtpAndFetch = async (code: string) => {
    if (!token || otpVerifying) return;

    try {
      setOtpVerifying(true);
      setOtpError(null);

      const data = await treatmentPlanService.getByToken(token, code);
      setTreatmentPlan(data);
      setCachedData(token, data);
      setRequiresOtp(false);
    } catch (err) {
      const axiosError = err as AxiosError;
      if (axiosError.response?.status === 401) {
        setOtpError(t("treatmentPlanReview.otpInvalidError"));
      } else {
        setOtpError(t("treatmentPlanReview.loadError"));
      }
      console.error("Error verifying OTP:", err);
    } finally {
      setOtpVerifying(false);
    }
  };

  // Handle OTP completion
  const handleOtpComplete = (code: string) => {
    setOtpCode(code);
    verifyOtpAndFetch(code);
  };

  // Initial fetch (useEffect avoids duplicate calls from Strict Mode remounts)
  useEffect(() => {
    if (!token || treatmentPlan || requiresOtp) return;

    const abortController = new AbortController();

    treatmentPlanService
      .getByToken(token, undefined, abortController.signal)
      .then((data) => {
        setTreatmentPlan(data);
        setCachedData(token, data);
        setLoading(false);
      })
      .catch((err) => {
        if (err instanceof Error && err.name === "CanceledError") return;

        const axiosError = err as AxiosError;
        if (axiosError.response?.status === 401) {
          setRequiresOtp(true);
          setLoading(false);
          sendOtpCode();
        } else {
          setError(t("treatmentPlanReview.loadError"));
          console.error("Error fetching treatment plan:", err);
          setLoading(false);
        }
      });

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only when token is set
  }, [token]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }) +
      "  |  " +
      date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    );
  };

  const handleRequestChanges = () => {
    if (!token) return;
    navigate(`/treatment-plan/review/rejected?token=${token}`);
  };

  const SIGNATURE_MODAL_ID = "signature-modal";

  const openPdfInNewTab = (base64: string) => {
    // Convert base64 to Blob
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    // Create URL and open in new tab
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
  };

  const handleSign = async (signature: string, method: "click" | "draw") => {
    if (!token || !treatmentPlan || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    try {
      setIsSubmitting(true);
      setError(null);

      // Generate signed PDF
      const { pdfFileBase64, pdfFileName } =
        await generateSignedTreatmentPlanPdf({
          treatmentPlan,
          signature,
          signatureMethod: method,
        });

      // Log PDF size for debugging
      const pdfSizeBytes = (pdfFileBase64.length * 3) / 4; // base64 to bytes approximation
      const pdfSizeKB = (pdfSizeBytes / 1024).toFixed(2);
      const pdfSizeMB = (pdfSizeBytes / (1024 * 1024)).toFixed(2);
      console.log(`📄 PDF Size: ${pdfSizeKB} KB (${pdfSizeMB} MB)`);
      console.log(`📄 PDF File Name: ${pdfFileName}`);

      // Send to API
      await treatmentPlanService.approveByPatient(
        token,
        pdfFileBase64,
        pdfFileName,
      );

      // Open signed PDF in new tab for viewing/download
      openPdfInNewTab(pdfFileBase64);

      sessionStorage.removeItem(getCacheKey(token));
      closeModal(SIGNATURE_MODAL_ID);
      navigate("/treatment-plan/review/approved");
    } catch (err) {
      setError(t("treatmentPlanReview.approveError"));
      console.error("Error approving treatment plan:", err);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleApproveAndSign = () => {
    if (!token || !treatmentPlan || isSubmittingRef.current) return;

    const patientFullName = [
      treatmentPlan.patientFirstName,
      treatmentPlan.patientLastName,
    ]
      .filter(Boolean)
      .join(" ");
    const patientNameForSign =
      patientFullName || t("treatmentPlanReview.patient");

    openModal({
      id: SIGNATURE_MODAL_ID,
      content: (
        <SignatureModalContent
          onSign={handleSign}
          onCancel={() => closeModal(SIGNATURE_MODAL_ID)}
          onLoadingChange={(loading) =>
            updateModal(SIGNATURE_MODAL_ID, {
              closeOnBackdropClick: !loading,
              showCloseButton: !loading,
            })
          }
          patientName={patientNameForSign}
        />
      ),
      showCloseButton: true,
      containerPadding: "24px",
      maxWidth: "sm",
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // OTP Verification Screen (using Auth pages layout)
  if (requiresOtp && !treatmentPlan) {
    return (
      <AuthPageLayout contentWidth="694px">
        <OtpVerificationContainer>
          <OtpVerificationTitle>
            {t("treatmentPlanReview.otpTitle")}
          </OtpVerificationTitle>

          <OtpVerificationSubtitle>
            {t("treatmentPlanReview.otpDescription")}
          </OtpVerificationSubtitle>

          <OtpVerificationForm>
            <VerificationCodeInput
              length={6}
              onComplete={handleOtpComplete}
              onChange={setOtpCode}
              error={!!otpError}
            />

            <FormErrorContainer errors={otpError ? [otpError] : []} />

            <Box sx={{ textAlign: "center" }}>
              {t("verification.didntGetCode")}{" "}
              {resendCooldown > 0 ? (
                <Box component="span" sx={{ color: "#666" }}>
                  {t("verification.resendIn", { seconds: resendCooldown })}
                </Box>
              ) : (
                <Button
                  variant="text"
                  onClick={sendOtpCode}
                  disabled={otpSending}
                  sx={{
                    minWidth: "auto",
                    p: 0,
                    textTransform: "none",
                    fontSize: "14px",
                    color: "primary.main",
                  }}
                >
                  {t("verification.resendCode")}
                </Button>
              )}
            </Box>

            <OtpVerificationActions>
              <Button
                variant="contained"
                onClick={() => verifyOtpAndFetch(otpCode)}
                disabled={otpCode.length !== 6 || otpVerifying}
              >
                {otpVerifying ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("verification.verify")
                )}
              </Button>
            </OtpVerificationActions>
          </OtpVerificationForm>
        </OtpVerificationContainer>
      </AuthPageLayout>
    );
  }

  if (error || !treatmentPlan) {
    return (
      <PageContainer>
        <ContentWrapper>
          <ErrorContainer>
            <ErrorText>{error || t("treatmentPlanReview.notFound")}</ErrorText>
          </ErrorContainer>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // Construct full names from firstName and lastName
  const patientName = [
    treatmentPlan.patientFirstName,
    treatmentPlan.patientLastName,
  ]
    .filter(Boolean)
    .join(" ");
  const providerName = [
    treatmentPlan.providerFirstName,
    treatmentPlan.providerLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const persons = [
    {
      name: patientName || t("treatmentPlanReview.patient"),
      role: t("treatmentPlanReview.patient"),
    },
    {
      name: providerName || t("treatmentPlanReview.provider"),
      role: t("treatmentPlanReview.initiatedBy"),
    },
  ];

  // Status 5 = ApprovedByPatient, Status 4 = DeclinedByPatient
  const isAlreadyProcessed =
    treatmentPlan.status === 5 || treatmentPlan.status === 4;

  // Check if treatment plan has options (any appointment with conditionalPath)
  // If any appointment has conditionalPath, total fee should be 0
  const hasOptions = treatmentPlan.appointments.some(
    (appointment) =>
      appointment.conditionalPath && appointment.conditionalPath.trim() !== "",
  );

  return (
    <ContentWrapper>
      <StickyHeader>
      <LogoContainer>
        <AppLogoIcon width="180px" height="40px" />
      </LogoContainer>

      <PageTitle>{treatmentPlan.name}</PageTitle>
      <DateInfo>{formatDateTime(treatmentPlan.dateGenerated)}</DateInfo>
      </StickyHeader>

      <PersonsInfo persons={persons} />

      <Section>
        <SectionTitle>{t("treatmentPlanReview.treatmentSummary")}</SectionTitle>
        <SectionContent
          dangerouslySetInnerHTML={{
            __html: treatmentPlan.treatmentSummary || "",
          }}
        />
      </Section>

      <Section>
        <SectionTitle>{t("treatmentPlanReview.treatmentSteps")}</SectionTitle>
        <StepsContainer>
          <TreatmentAppointmentsTableHeader showActionsColumn={false} />
          {(treatmentPlan.appointments || [])
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((appointment, index, sortedAppointments) => (
              <React.Fragment key={appointment.id}>
                <TreatmentAppointment
                  appointment={appointment}
                  appointmentNumber={index + 1}
                  isLastAppointment={index === sortedAppointments.length - 1}
                  showTooltip={isDesktop}
                  showActionsColumn={false}
                />
              </React.Fragment>
            ))}
        </StepsContainer>
      </Section>

      <TotalFeeContainer>
        <TotalFeeLabel>{t("treatmentPlanReview.totalFee")}</TotalFeeLabel>
        <TotalFeeValue>
          $
          {hasOptions
            ? "0.00"
            : formatTotalFeeWithCents(treatmentPlan.totalFee || 0)}
        </TotalFeeValue>
      </TotalFeeContainer>

      {!isAlreadyProcessed && (
        <ActionButtonsContainer>
          <Button
            variant="contained"
            onClick={handleRequestChanges}
            disabled={isSubmitting}
            fullWidth
            sx={{
              backgroundColor: colors.status.error,
              "&:hover": {
                backgroundColor: colors.status.error,
                opacity: 0.9,
              },
              "@media (min-width: 601px)": {
                width: "auto",
              },
            }}
          >
            {isSubmitting
              ? t("treatmentPlanReview.submitting")
              : t("treatmentPlanReview.rejectButton")}
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveAndSign}
            disabled={isSubmitting}
            fullWidth
            sx={{
              backgroundColor: colors.amber.main,
              "&:hover": {
                backgroundColor: colors.amber.dark,
              },
              "@media (min-width: 601px)": {
                width: "auto",
              },
            }}
          >
            {isSubmitting
              ? t("treatmentPlanReview.submitting")
              : t("treatmentPlanReview.approveButton")}
          </Button>
        </ActionButtonsContainer>
      )}

      <Footer>
        <FooterDate>
          {t("treatmentPlanReview.fileGenerated")}{" "}
          {new Date(treatmentPlan.dateGenerated).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })}
        </FooterDate>
        <FooterClinicName>
          {t("treatmentPlanReview.clinicName")}
        </FooterClinicName>
        <FooterContacts>
          {t("treatmentPlanReview.clinicContacts")}
        </FooterContacts>
      </Footer>
    </ContentWrapper>
  );
};

export default TreatmentPlanReviewPage;
